<script setup>
import { ref, computed, watch, onMounted, onUnmounted } from 'vue';
import { useAlert } from 'dashboard/composables';
import { useToggle } from '@vueuse/core';
import { useI18n } from 'vue-i18n';
import { useStore, useStoreGetters } from 'dashboard/composables/store';
import { useEmitter } from 'dashboard/composables/emitter';
import { useKeyboardEvents } from 'dashboard/composables/useKeyboardEvents';

import WootDropdownItem from 'shared/components/ui/dropdown/DropdownItem.vue';
import WootDropdownMenu from 'shared/components/ui/dropdown/DropdownMenu.vue';
import EmailTranscriptModal from '../widgets/conversation/EmailTranscriptModal.vue';
import wootConstants from 'dashboard/constants/globals';
import {
  CMD_REOPEN_CONVERSATION,
  CMD_RESOLVE_CONVERSATION,
  CMD_MUTE_CONVERSATION,
  CMD_UNMUTE_CONVERSATION,
  CMD_SEND_TRANSCRIPT,
} from 'dashboard/helper/commandbar/events';

const store = useStore();
const getters = useStoreGetters();
const { t } = useI18n();

const arrowDownButtonRef = ref(null);
const isLoading = ref(false);
const dropdownPosition = ref({ top: 0, right: 0 });

const [showActionsDropdown, toggleDropdown] = useToggle();
const closeDropdown = () => toggleDropdown(false);
const openDropdown = () => {
  updateDropdownPosition();
  toggleDropdown(true);
};

const updateDropdownPosition = () => {
  if (!arrowDownButtonRef.value?.$el) return;
  const buttonEl = arrowDownButtonRef.value.$el;
  const rect = buttonEl.getBoundingClientRect();
  dropdownPosition.value = {
    top: rect.bottom + 4,
    right: window.innerWidth - rect.right,
  };
};

watch(showActionsDropdown, (isOpen) => {
  if (isOpen) {
    updateDropdownPosition();
    window.addEventListener('scroll', updateDropdownPosition, true);
    window.addEventListener('resize', updateDropdownPosition);
  } else {
    window.removeEventListener('scroll', updateDropdownPosition, true);
    window.removeEventListener('resize', updateDropdownPosition);
  }
});

onUnmounted(() => {
  window.removeEventListener('scroll', updateDropdownPosition, true);
  window.removeEventListener('resize', updateDropdownPosition);
});

const [showEmailActionsModal, toggleEmailActionsModal] = useToggle();

const currentChat = computed(() => getters.getSelectedChat.value);

const isOpen = computed(
  () => currentChat.value.status === wootConstants.STATUS_TYPE.OPEN
);
const isPending = computed(
  () => currentChat.value.status === wootConstants.STATUS_TYPE.PENDING
);
const isResolved = computed(
  () => currentChat.value.status === wootConstants.STATUS_TYPE.RESOLVED
);
const isSnoozed = computed(
  () => currentChat.value.status === wootConstants.STATUS_TYPE.SNOOZED
);

const buttonClass = computed(() => {
  if (isPending.value) return 'primary';
  if (isOpen.value) return 'success';
  if (isResolved.value) return 'warning';
  return '';
});

const showAdditionalActions = computed(
  () => !isPending.value && !isSnoozed.value
);

const showOpenButton = computed(() => {
  return isPending.value || isSnoozed.value;
});

const getConversationParams = () => {
  const allConversations = document.querySelectorAll(
    '.conversations-list .conversation'
  );

  const activeConversation = document.querySelector(
    'div.conversations-list div.conversation.active'
  );
  const activeConversationIndex = [...allConversations].indexOf(
    activeConversation
  );
  const lastConversationIndex = allConversations.length - 1;

  return {
    all: allConversations,
    activeIndex: activeConversationIndex,
    lastIndex: lastConversationIndex,
  };
};

const openSnoozeModal = () => {
  const ninja = document.querySelector('ninja-keys');
  ninja.open({ parent: 'snooze_conversation' });
};

const toggleStatus = (status, snoozedUntil) => {
  closeDropdown();
  isLoading.value = true;
  store
    .dispatch('toggleStatus', {
      conversationId: currentChat.value.id,
      status,
      snoozedUntil,
    })
    .then(() => {
      useAlert(t('CONVERSATION.CHANGE_STATUS'), {type: 'success'});
      isLoading.value = false;
    });
};

const onCmdOpenConversation = () => {
  toggleStatus(wootConstants.STATUS_TYPE.OPEN);
};

const onCmdResolveConversation = () => {
  toggleStatus(wootConstants.STATUS_TYPE.RESOLVED);
};

const mute = () => {
  closeDropdown();
  store.dispatch('muteConversation', currentChat.value.id);
  useAlert(t('CONTACT_PANEL.MUTED_SUCCESS'), { type: 'success' });
};

const unmute = () => {
  closeDropdown();
  store.dispatch('unmuteConversation', currentChat.value.id);
  useAlert(t('CONTACT_PANEL.UNMUTED_SUCCESS'), { type: 'success' });
};

const handleSendTranscript = () => {
  closeDropdown();
  toggleEmailActionsModal();
};

const keyboardEvents = {
  'Alt+KeyM': {
    action: () => arrowDownButtonRef.value?.$el.click(),
    allowOnFocusedInput: true,
  },
  'Alt+KeyE': {
    action: async () => {
      await toggleStatus(wootConstants.STATUS_TYPE.RESOLVED);
    },
  },
  '$mod+Alt+KeyE': {
    action: async event => {
      const { all, activeIndex, lastIndex } = getConversationParams();
      await toggleStatus(wootConstants.STATUS_TYPE.RESOLVED);

      if (activeIndex < lastIndex) {
        all[activeIndex + 1].click();
      } else if (all.length > 1) {
        all[0].click();
        document.querySelector('.conversations-list').scrollTop = 0;
      }
      event.preventDefault();
    },
  },
};

useKeyboardEvents(keyboardEvents);

useEmitter(CMD_REOPEN_CONVERSATION, onCmdOpenConversation);
useEmitter(CMD_RESOLVE_CONVERSATION, onCmdResolveConversation);
useEmitter(CMD_MUTE_CONVERSATION, mute);
useEmitter(CMD_UNMUTE_CONVERSATION, unmute);
useEmitter(CMD_SEND_TRANSCRIPT, handleSendTranscript);
</script>

<template>
  <div class="relative flex items-center justify-end resolve-actions">
    <div class="button-group flex items-center gap-1">
      <woot-button
        v-if="isOpen"
        v-tooltip="$t('CONVERSATION.HEADER.RESOLVE_ACTION')"
        class-names="resolve"
        color-scheme="success"
        size="small"
        icon="checkmark"
        :is-loading="isLoading"
        @click="onCmdResolveConversation"
      >
      <span class="hidden md:inline">{{ $t('CONVERSATION.HEADER.RESOLVE_ACTION') }}</span>
      </woot-button>
      <woot-button
        v-else-if="isResolved"
        v-tooltip="$t('CONVERSATION.HEADER.REOPEN_ACTION')"
        class-names="resolve"
        color-scheme="warning"
        size="small"
        icon="arrow-redo"
        :is-loading="isLoading"
        @click="onCmdOpenConversation"
      >
        <span class="hidden md:inline">{{ $t('CONVERSATION.HEADER.REOPEN_ACTION') }}</span>
      </woot-button>
      <woot-button
        v-else-if="showOpenButton"
        v-tooltip="$t('CONVERSATION.HEADER.OPEN_ACTION')"
        class-names="resolve"
        color-scheme="primary"
        size="small"
        icon="person"
        :is-loading="isLoading"
        @click="onCmdOpenConversation"
      >
        <span class="hidden md:inline">{{ $t('CONVERSATION.HEADER.OPEN_ACTION') }}</span>
      </woot-button>
      <woot-button
        v-if="showAdditionalActions"
        ref="arrowDownButtonRef"
        :color-scheme="buttonClass"
        size="small"
        :disabled="isLoading"
        icon="chevron-down"
        @click="openDropdown"
      />
    </div>
    <Teleport to="body">
      <div
        v-if="showActionsDropdown"
        v-on-clickaway="closeDropdown"
        class="dropdown-pane dropdown-pane--open fixed max-w-[12.5rem] min-w-[9.75rem] z-[9999]"
        :style="{
          top: `${dropdownPosition.top}px`,
          right: `${dropdownPosition.right}px`,
        }"
      >
        <WootDropdownMenu class="mb-0">
        <WootDropdownItem v-if="!isPending">
          <woot-button
            variant="clear"
            color-scheme="secondary"
            size="small"
            icon="snooze"
            @click="() => openSnoozeModal()"
          >
            {{ t('CONVERSATION.RESOLVE_DROPDOWN.SNOOZE_UNTIL') }}
          </woot-button>
        </WootDropdownItem>
        <WootDropdownItem v-if="!isPending">
          <woot-button
            variant="clear"
            color-scheme="secondary"
            size="small"
            icon="book-clock"
            @click="() => toggleStatus(wootConstants.STATUS_TYPE.PENDING)"
          >
            {{ t('CONVERSATION.RESOLVE_DROPDOWN.MARK_PENDING') }}
          </woot-button>
        </WootDropdownItem>
        <WootDropdownItem>
          <woot-button
            v-if="!currentChat.muted"
            variant="clear"
            color-scheme="secondary"
            size="small"
            icon="speaker-mute"
            @click="mute"
          >
            {{ t('CONTACT_PANEL.MUTE_CONTACT') }}
          </woot-button>
          <woot-button
            v-else
            variant="clear"
            color-scheme="secondary"
            size="small"
            icon="speaker-1"
            @click="unmute"
          >
            {{ t('CONTACT_PANEL.UNMUTE_CONTACT') }}
          </woot-button>
        </WootDropdownItem>
        <WootDropdownItem>
          <woot-button
            variant="clear"
            color-scheme="secondary"
            size="small"
            icon="share"
            @click="handleSendTranscript"
          >
            {{ t('CONTACT_PANEL.SEND_TRANSCRIPT') }}
          </woot-button>
        </WootDropdownItem>
      </WootDropdownMenu>
      </div>
    </Teleport>
    <EmailTranscriptModal
      v-if="showEmailActionsModal"
      :show="showEmailActionsModal"
      :current-chat="currentChat"
      @cancel="toggleEmailActionsModal"
    />
  </div>
</template>

<style lang="scss" scoped>
.dropdown-pane {
  .dropdown-menu__item {
    @apply mb-0;
  }
}
</style>
